/**
 * Extracts the content date for month-filtering purposes.
 *
 * Priority:
 * 1. Trello due date (if set)
 * 2. Date pattern in card name: "DD/MM - ..." or "DD/MM/YYYY - ..."
 *
 * Returns null if neither is available.
 */
export function extractContentDate(
  cardName: string,
  due: string | null,
  selectedYear: number
): Date | null {
  // 1. Try due date
  if (due) {
    const d = new Date(due)
    if (!isNaN(d.getTime())) return d
  }

  // 2. Try to parse "DD/MM" or "DD/MM/YYYY" from start of card name
  // Patterns:  "05/08 - ..."  or  "05/08/2026 - ..."  or  "5/8 - ..."
  const match = cardName.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?/)
  if (match) {
    const day = parseInt(match[1], 10)
    const month = parseInt(match[2], 10) - 1 // JS months are 0-indexed
    const year = match[3] ? parseInt(match[3], 10) : selectedYear

    if (day >= 1 && day <= 31 && month >= 0 && month <= 11) {
      const d = new Date(year, month, day)
      if (!isNaN(d.getTime())) return d
    }
  }

  return null
}

/**
 * Returns true if the given date falls within the selected month and year.
 */
export function isInMonth(date: Date | null, month: number, year: number): boolean {
  if (!date) return false
  return date.getFullYear() === year && date.getMonth() === month
}

/**
 * Formats a Date to a localised "DD/MM/YYYY" string in pt-BR.
 */
export function formatDateBR(date: Date | null): string {
  if (!date) return '—'
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/**
 * Returns the month AFTER the given month/year (0-indexed months).
 * Correctly wraps December (11) → January (0) of the next year.
 *
 * Examples:
 *   getNextMonth(6, 2026)   →  { month: 7, year: 2026 }  // August
 *   getNextMonth(11, 2026)  →  { month: 0, year: 2027 }  // January
 */
export function getNextMonth(month: number, year: number): { month: number; year: number } {
  if (month === 11) {
    return { month: 0, year: year + 1 }
  }
  return { month: month + 1, year }
}

/**
 * Returns a list of {label, value} options for the month selector,
 * covering the last 2 months + next 6 months relative to today.
 */
export function getMonthOptions(): Array<{ label: string; month: number; year: number }> {
  const today = new Date()
  const options: Array<{ label: string; month: number; year: number }> = []

  for (let offset = -2; offset <= 6; offset++) {
    const d = new Date(today.getFullYear(), today.getMonth() + offset, 1)
    options.push({
      label: d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
      month: d.getMonth(),
      year: d.getFullYear(),
    })
  }

  return options
}
